// Janela semanal de envio do encaminhamento (fuso America/Sao_Paulo).
//
// Cada dia da semana (0 = domingo ... 6 = sabado, igual a Date#getDay) tem um
// intervalo [inicio, fim) em minutos do dia, ou null (dia fechado). Fora da
// janela a oferta e descartada ('drop') ou enfileirada para a proxima abertura
// ('queue').
//
// ponytail: o calculo da proxima abertura assume offset fixo do fuso nos
// proximos 7 dias. Vale para America/Sao_Paulo (sem horario de verao desde
// 2019); se o fuso passar a ter DST, recalcular o offset por instante.
export const FORWARDING_TIMEZONE = "America/Sao_Paulo";
export const MINUTES_PER_DAY = 24 * 60;
const BUSINESS_DAY = { start: 8 * 60, end: 22 * 60 };
export const DEFAULT_WINDOW = Object.freeze({
    enabled: false,
    outside: "drop",
    schedule: Object.freeze([
        BUSINESS_DAY,
        BUSINESS_DAY,
        BUSINESS_DAY,
        BUSINESS_DAY,
        BUSINESS_DAY,
        BUSINESS_DAY,
        BUSINESS_DAY,
    ]),
});
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$|^24:00$/;
/** "HH:MM" -> minutos do dia. Retorna null se invalido. Aceita "24:00" (fim do dia). */
export function parseHhmm(value) {
    if (!HHMM.test(value)) {
        return null;
    }
    const [h, m] = value.split(":").map(Number);
    return h * 60 + m;
}
/** Minutos do dia -> "HH:MM". */
export function formatHhmm(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function normalizeInterval(raw) {
    if (raw === null || typeof raw !== "object") {
        return null;
    }
    const r = raw;
    const start = typeof r["start"] === "string" ? parseHhmm(r["start"]) : null;
    const end = typeof r["end"] === "string" ? parseHhmm(r["end"]) : null;
    if (start === null || end === null || start >= end) {
        return null;
    }
    return { start, end };
}
/** Normaliza o jsonb cru ({enabled, outside, schedule: {"0": {start,end}|null}}). */
export function normalizeWindow(raw) {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return DEFAULT_WINDOW;
    }
    const r = raw;
    const scheduleRaw = r["schedule"] !== null && typeof r["schedule"] === "object"
        ? r["schedule"]
        : {};
    const schedule = Array.from({ length: 7 }, (_, day) => normalizeInterval(scheduleRaw[String(day)]));
    return {
        enabled: r["enabled"] === true,
        outside: r["outside"] === "queue" ? "queue" : "drop",
        schedule,
    };
}
/** Serializa a janela no formato jsonb persistido. */
export function serializeWindow(window) {
    const schedule = {};
    window.schedule.forEach((interval, day) => {
        schedule[String(day)] = interval
            ? { start: formatHhmm(interval.start), end: formatHhmm(interval.end) }
            : null;
    });
    return { enabled: window.enabled, outside: window.outside, schedule };
}
const WEEKDAY_INDEX = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: FORWARDING_TIMEZONE,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});
/** Dia da semana e minuto do dia no fuso do encaminhamento. */
export function localParts(now) {
    const parts = formatter.formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
    const weekday = WEEKDAY_INDEX[get("weekday")] ?? 0;
    const minute = Number(get("hour")) * 60 + Number(get("minute"));
    return { weekday, minute };
}
function isOpenAt(window, weekday, minute) {
    const interval = window.schedule[weekday];
    return (interval !== null && interval !== undefined && minute >= interval.start && minute < interval.end);
}
/** Minutos ate a proxima abertura a partir de (weekday, minute). null = nenhum dia aberto. */
export function minutesUntilNextOpening(window, weekday, minute) {
    for (let offset = 0; offset <= 7; offset++) {
        const interval = window.schedule[(weekday + offset) % 7];
        if (!interval) {
            continue;
        }
        if (offset === 0 && minute >= interval.start) {
            continue;
        }
        return offset * MINUTES_PER_DAY - minute + interval.start;
    }
    return null;
}
/** Avalia se `now` esta dentro da janela e, se nao, o que fazer. */
export function evaluateWindow(window, now) {
    if (!window.enabled) {
        return { open: true };
    }
    const { weekday, minute } = localParts(now);
    if (isOpenAt(window, weekday, minute)) {
        return { open: true };
    }
    if (window.outside === "drop") {
        return { open: false, action: "drop" };
    }
    const delta = minutesUntilNextOpening(window, weekday, minute);
    if (delta === null) {
        // Fila sem nenhum dia aberto nunca seria liberada: descarta.
        return { open: false, action: "drop" };
    }
    const startOfMinute = now.getTime() - (now.getTime() % 60_000);
    return { open: false, action: "queue", releaseAt: new Date(startOfMinute + delta * 60_000) };
}
//# sourceMappingURL=window.js.map